import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { VA_COMP_RATES_2026, evidenceElements } from "./claimData";
import { nexusRequirementWaived } from "./nexusWaiver";
import { getNexusCoverage } from "./riskEngine";

export const uid = () => Math.random().toString(36).slice(2, 10);

export function combineRatings(ratings) {
  const sorted = ratings.map(Number).filter((r) => r > 0).sort((a, b) => b - a);
  let combined = 0;
  sorted.forEach((rating) => { combined += (100 - combined) * (rating / 100); });
  return { raw: Math.round(combined), rounded: Math.min(100, Math.round(combined / 10) * 10), exact: combined };
}

export function combineRatingsWithBilateral(issues) {
  const compensable = issues.map((issue) => ({ ...issue, rating: Number(issue.estimatedRating || 0) })).filter((issue) => issue.rating > 0);
  const isEligibleSide = (issue) => ["Left", "Right", "Bilateral"].includes(issue.bilateralSide);
  const upper = compensable.filter((issue) => issue.pairedExtremity === "Upper extremities" && isEligibleSide(issue));
  const lower = compensable.filter((issue) => issue.pairedExtremity === "Lower extremities" && isEligibleSide(issue));
  const qualifies = (group) => { const sides = new Set(group.map((i) => i.bilateralSide)); return sides.has("Bilateral") || (sides.has("Left") && sides.has("Right")); };
  const eligible = [];
  if (qualifies(upper)) eligible.push(...upper);
  if (qualifies(lower)) eligible.push(...lower);
  const eligibleIds = new Set(eligible.map((i) => i.id));
  const nonBilateral = compensable.filter((i) => !eligibleIds.has(i.id)).map((i) => i.rating);
  if (!eligible.length) return { ...combineRatings(compensable.map((i) => i.rating)), bilateralApplied: false, bilateralBase: 0, bilateralAdded: 0, bilateralCombinedWithFactor: 0, eligibleBilateralIssues: [] };
  const bilateralBaseExact = combineRatings(eligible.map((i) => i.rating)).exact;
  const bilateralAdded = bilateralBaseExact * 0.1;
  const final = combineRatings([bilateralBaseExact + bilateralAdded, ...nonBilateral]);
  return { ...final, bilateralApplied: true, bilateralBase: Math.round(bilateralBaseExact), bilateralAdded: Math.round(bilateralAdded * 10) / 10, bilateralCombinedWithFactor: Math.round((bilateralBaseExact + bilateralAdded) * 10) / 10, eligibleBilateralIssues: eligible };
}

export function money(value) {
  return Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

export function estimateVACompensation(rating, maritalStatus = "Single", minorDependents = 0) {
  const rounded = Math.max(0, Math.min(100, Math.round(Number(rating || 0) / 10) * 10));
  const table = VA_COMP_RATES_2026[rounded] || VA_COMP_RATES_2026[0];
  const kids = Math.max(0, Number(minorDependents || 0));
  const married = maritalStatus === "Married";
  let monthly = table.alone;
  let basis = "Veteran alone";
  if (rounded >= 30) {
    if (married && kids > 0) { monthly = table.spouseOneChild + Math.max(0, kids - 1) * table.additionalChild; basis = kids === 1 ? "Veteran with spouse and 1 child" : `Veteran with spouse and ${kids} children`; }
    else if (married) { monthly = table.spouse; basis = "Veteran with spouse"; }
    else if (kids > 0) { monthly = table.childOnly + Math.max(0, kids - 1) * table.additionalChild; basis = kids === 1 ? "Veteran with 1 child" : `Veteran with ${kids} children`; }
  } else if (rounded > 0) { basis = "10-20% ratings do not increase for dependents"; }
  return { monthly, yearly: monthly * 12, rating: rounded, basis };
}

export function estimateHigh3Retirement(high3Average, yearsService, monthsService = 0) {
  const years = Math.max(0, Number(yearsService || 0)) + Math.max(0, Number(monthsService || 0)) / 12;
  const multiplier = years * 0.025;
  const monthly = Math.max(0, Number(high3Average || 0)) * multiplier;
  return { years, multiplier, monthly, yearly: monthly * 12 };
}

export function calculateReadiness(issues, evidence = [], ctx = {}) {
  if (!issues.length) return 0;
  return Math.round(
    (issues.reduce((sum, issue) =>
      sum + evidenceElements.filter((el) => computePillarStatus(issue, el.id, evidence, ctx) === "have").length, 0
    ) / (issues.length * evidenceElements.length)) * 100
  );
}

// Compute the status of a single proof pillar for an issue.
// Simplified two-state model: "have" if at least one piece of evidence is attached for that pillar,
// "need" otherwise. We honor any pre-existing manual fallback as well (legacy data).
//
// Special case: the "nexus" pillar is auto-satisfied (returned as "have") when
// the workspace claim posture qualifies for a nexus waiver — i.e. BDD / pre-
// discharge, or FDC filed within 1 year of separation. See lib/nexusWaiver.js.
export function computePillarStatus(issue, elementId, evidence = [], ctx = {}) {
  // Nexus pillar: theory-aware coverage. Direct/BDD/presumptive theories
  // get nexus covered when in-service evidence is on file (the in-service
  // medical record IS the link). Plus the standard regulatory waiver path.
  if (elementId === "nexus") {
    if (nexusRequirementWaived(ctx.workspace, ctx.master526ez)) return "have";
    const cov = getNexusCoverage(issue, evidence, ctx);
    if (cov.covered) return "have";
  }
  const attached = evidence.filter((ev) => ev.issueIds?.includes(issue.id) && ev.element === elementId);
  if (attached.length) return "have";
  // Legacy manual fallback: any non-"missing" value still counts as "have."
  const manual = issue.evidence?.[elementId];
  if (manual && manual !== "missing" && manual !== "need") return "have";
  return "need";
}

export function getEvidenceGaps(issues, evidence = [], ctx = {}) {
  const gaps = [];
  issues.forEach((issue) => {
    evidenceElements.forEach((el) => {
      const status = computePillarStatus(issue, el.id, evidence, ctx);
      if (status === "need") {
        gaps.push({ issueId: issue.id, issueName: issue.claimedAs, element: el.id, elementLabel: el.label, status, help: el.help });
      }
    });
  });
  return gaps;
}

export function validateClaimPacket({ issues, evidence, master526ez, form0781 }) {
  const failures = [];
  const warnings = [];
  if (!issues.length) failures.push("No claimed conditions have been added.");
  if (!master526ez.claimedConditions?.length) failures.push("Master 21-526EZ has no claimed conditions listed.");
  issues.forEach((issue) => {
    if (!master526ez.claimedConditions?.some((c) => c.issueId === issue.id)) warnings.push(`${issue.claimedAs} is not on the master 21-526EZ worksheet.`);
    const attached = evidence.filter((ev) => ev.issueIds?.includes(issue.id));
    if (!attached.length) failures.push(`${issue.claimedAs} has no evidence attached.`);
    evidenceElements.forEach((el) => { if (!attached.some((ev) => ev.element === el.id)) warnings.push(`${issue.claimedAs} has no evidence for: ${el.label}.`); });
  });
  if (issues.some((issue) => issue.system === "mental" || /ptsd|anxiety|depress|mental/i.test(issue.claimedAs))) {
    if (!form0781.eventDescription?.trim()) warnings.push("Master 21-0781 is missing the traumatic event description.");
    if (!form0781.currentImpact?.trim()) warnings.push("Master 21-0781 is missing current impact.");
  }
  evidence.forEach((ev) => {
    if (!ev.title?.trim()) failures.push("An evidence item is missing a title.");
    if (!ev.issueIds?.length) failures.push(`${ev.title || "An evidence item"} is not attached to a condition.`);
    if (!ev.element?.trim()) failures.push(`${ev.title || "An evidence item"} is not mapped to a proof element.`);
  });
  return { failures, warnings, score: Math.max(0, 100 - failures.length * 15 - warnings.length * 5) };
}

// Disclaimer text included in every generated PDF
const DISCLAIMER_PARAGRAPHS = [
  "NO LIABILITY. The creator(s) of this application accept no liability whatsoever for any outcomes, decisions, errors, or consequences resulting from the use of this tool. You use this application entirely at your own risk.",
  "NOT OFFICIAL VA FORMS. All documents, worksheets, and PDFs generated by this application are unofficial planning tools only. They are NOT official VA forms and must NOT be submitted to the VA as-is. After using this tool to organize your information, you must complete and submit the correct official VA forms available at VA.gov/find-forms.",
  "NOT LEGAL, MEDICAL, OR PROFESSIONAL ADVICE. This application does not provide legal advice, medical advice, or any form of professional counsel. It is not affiliated with the Department of Veterans Affairs, any Veterans Service Organization (VSO), accredited claims agent, attorney, or medical professional. No attorney-client, doctor-patient, or representative-claimant relationship is created by using this tool.",
  "VERIFY EVERYTHING. All rating criteria, diagnostic codes, compensation rates, form versions, deadlines, and regulatory references must be independently verified against official sources (VA.gov, eCFR.gov) before taking any action. Information may be outdated, incomplete, or inaccurate.",
  "THIS TOOL IS FOR PLANNING AND ORGANIZATION ONLY. It was created to assist veterans in gathering and organizing their claim information in one place. It does not file claims, communicate with the VA, or make any representations on your behalf.",
];

function wrapText(text, max) {
  const words = String(text || "").split(" ").filter(Boolean);
  const rows = [];
  let row = "";
  words.forEach((w) => {
    if ((row + " " + w).trim().length > max) { rows.push(row); row = w; }
    else row = (row + " " + w).trim();
  });
  if (row) rows.push(row);
  return rows;
}

async function appendDisclaimerPage(pdfDoc, font, bold) {
  const page = pdfDoc.addPage([612, 792]);
  const draw = (text, x, y, size = 10, isBold = false) =>
    page.drawText(String(text || ""), { x, y, size, font: isBold ? bold : font, color: rgb(0.05, 0.09, 0.16) });
  let y = 748;
  draw("IMPORTANT DISCLAIMER", 54, y, 16, true); y -= 22;
  draw("Read carefully before relying on this document.", 54, y, 9); y -= 20;
  DISCLAIMER_PARAGRAPHS.forEach((para) => {
    const lines = wrapText(para, 95);
    lines.forEach((l, idx) => {
      if (y < 60) return;
      draw(l, 54, y, 9, idx === 0);
      y -= 12;
    });
    y -= 8;
  });
}

function drawFooter(page, font) {
  page.drawText("Unofficial planning worksheet - NOT an official VA form. See disclaimer page.", {
    x: 54, y: 30, size: 7, font, color: rgb(0.4, 0.45, 0.55),
  });
}

export async function createClaimOps526EZPdf({ master526ez, profile, workspace, issues }) {
  const pdfDoc = await PDFDocument.create();
  let page = pdfDoc.addPage([612, 792]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const pages = [page];
  const draw = (text, x, y, size = 10, isBold = false) => page.drawText(String(text || ""), { x, y, size, font: isBold ? bold : font, color: rgb(0.05, 0.09, 0.16) });
  const line = (x1, y1, x2, y2) => page.drawLine({ start: { x: x1, y: y1 }, end: { x: x2, y: y2 }, thickness: 0.7, color: rgb(0.65, 0.7, 0.78) });
  let y = 748;
  draw("ClaimOps VA - 21-526EZ Worksheet", 54, y, 16, true); y -= 18;
  draw("Unofficial planning tool. Verify against the official VA Form 21-526EZ before submission. See disclaimer at end.", 54, y, 8); y -= 22;
  draw("Veteran / Claimant Information", 54, y, 12, true); y -= 16;
  const rows = [["Name", master526ez.veteranName || profile.name], ["Email", master526ez.email], ["Claim Type", master526ez.claimType || workspace.claimType], ["Address", master526ez.mailingAddress]];
  rows.forEach(([label, value]) => { draw(label, 54, y, 9, true); draw(String(value || ""), 190, y, 9); line(190, y - 2, 555, y - 2); y -= 18; });
  y -= 8; draw("Claimed Conditions", 54, y, 12, true); y -= 16;
  const conditions = master526ez.claimedConditions?.length ? master526ez.claimedConditions : issues.map((i) => ({ claimedAs: i.claimedAs, diagnosticReference: i.code, theory: i.theory, onset: i.onset, notes: i.functionalImpact }));
  conditions.forEach((c, idx) => {
    if (y < 95) { page = pdfDoc.addPage([612, 792]); pages.push(page); y = 748; }
    draw(`${idx + 1}. ${c.claimedAs || "Condition"}`, 54, y, 10, true); y -= 13;
    draw(`Ref: ${c.diagnosticReference || ""} | Theory: ${c.theory || ""}`, 72, y, 8); y -= 12;
    wrapText(c.notes || c.onset || "", 92).slice(0, 3).forEach((l) => { draw(l, 72, y, 8); y -= 11; });
    y -= 6;
  });
  pages.forEach((p) => drawFooter(p, font));
  await appendDisclaimerPage(pdfDoc, font, bold);
  return await pdfDoc.save();
}

export async function createEvidenceIndexPdf({ issues, evidence, workspace, profile }) {
  const pdfDoc = await PDFDocument.create();
  let page = pdfDoc.addPage([612, 792]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const pages = [page];
  const draw = (text, x, y, size = 10, b = false) => page.drawText(String(text || ""), { x, y, size, font: b ? bold : font, color: rgb(0.05, 0.09, 0.16) });
  let y = 740;
  draw("ClaimOps VA Evidence Index", 54, y, 18, true); y -= 24;
  draw(`Workspace: ${workspace.title || "VA Claim"}`, 54, y, 10); y -= 16;
  draw(`Veteran: ${profile.name || ""}`, 54, y, 10); y -= 16;
  draw("Unofficial planning index. See disclaimer at end.", 54, y, 8); y -= 18;
  issues.forEach((issue) => {
    if (y < 90) { page = pdfDoc.addPage([612, 792]); pages.push(page); y = 740; }
    draw(`${issue.claimedAs}`, 54, y, 12, true); y -= 16;
    draw(`Theory: ${issue.theory || ""} | DC: ${issue.code || ""}`, 72, y, 9); y -= 14;
    const items = evidence.filter((ev) => ev.issueIds?.includes(issue.id));
    if (!items.length) { draw("No evidence logged.", 72, y, 9); y -= 14; }
    items.forEach((ev) => { if (y < 90) { page = pdfDoc.addPage([612, 792]); pages.push(page); y = 740; } draw(`- ${ev.title || "Evidence"} | ${ev.source || ""} | ${ev.fileName || ""}`, 72, y, 9); y -= 12; if (ev.summary) { draw(`  ${String(ev.summary).slice(0, 95)}`, 84, y, 8); y -= 12; } });
    y -= 10;
  });
  pages.forEach((p) => drawFooter(p, font));
  await appendDisclaimerPage(pdfDoc, font, bold);
  return await pdfDoc.save();
}

export function buildClaimSummary({ workspace, profile, financials, issues, evidence, combined }) {
  const va = estimateVACompensation(combined.rounded, profile.maritalStatus, profile.minorDependents);
  const retired = estimateHigh3Retirement(financials.high3Average, financials.yearsService, financials.monthsService);
  const readiness = calculateReadiness(issues);
  return [
    "CLAIMOPS VA CLAIM PACKAGE",
    `Workspace: ${workspace.title}`,
    `Claim type: ${workspace.claimType}`,
    `Evidence readiness: ${readiness}%`,
    `VA math estimate: ${combined.rounded}%`,
    `Estimated VA compensation: ${money(va.monthly)} / month`,
    `Estimated High-3 retired pay: ${money(retired.monthly)} / month`,
    "",
    "CLAIMED CONDITIONS",
    ...issues.map((i, idx) => `${idx + 1}. ${i.claimedAs} | ${i.theory} | DC ${i.code} | Est. ${i.estimatedRating || 0}%`),
    "",
    "EVIDENCE INDEX",
    ...evidence.map((ev, idx) => `${idx + 1}. ${ev.title} | ${ev.source} | ${ev.fileName || "No file"}`),
    "",
    "================================================================",
    "IMPORTANT DISCLAIMER",
    "================================================================",
    ...DISCLAIMER_PARAGRAPHS.flatMap((p) => [p, ""]),
  ].join("\n");
}

export function saveBytesAsPdf(bytes, filename) {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadTextFile(filename, content, type = "text/plain;charset=utf-8") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ---- PDF Template Fill / 526EZ Import Parser were removed since import functionality is no longer offered ----
export const statementTypes = [
  {
    id: "personal",
    title: "Personal Statement",
    description: "Your own factual explanation of onset, service event, symptoms, continuity, and current impact.",
    formNumber: "VA Form 21-4138 or free-form",
    whoWrites: "You (the veteran)",
    whenToUse: "Use for almost every claim - it's how YOU tell the VA what happened and how this condition affects you.",
  },
  {
    id: "lay-witness",
    title: "Lay/Witness Statement",
    description: "A spouse, friend, coworker, or family member explains what they personally observed.",
    formNumber: "VA Form 21-10210",
    whoWrites: "A non-medical witness (spouse, parent, friend, coworker, battle buddy)",
    whenToUse: "Use when someone OTHER than you can confirm symptoms, behavior changes, or events they witnessed personally.",
  },
  {
    id: "nexus",
    title: "Nexus Statement",
    description: "Medical provider's opinion linking the current diagnosis to an in-service event, injury, or exposure (uses 'at least as likely as not' language).",
    formNumber: "Independent medical opinion letter",
    whoWrites: "A licensed medical or mental-health provider (MD, DO, NP, PA, PhD, LCSW, etc.)",
    whenToUse: "Use when VA disputes service-connection. The nexus opinion is often the single most important piece of evidence in a denied or appealed claim.",
  },
];

// Per-prompt coaching: tip = what reviewers look for; example = a concrete starter sentence.
export const statementPromptCoaching = {
  // Personal
  "personal:onset": {
    tip: "Anchor symptoms to a specific date or window in service. VA reviewers look for clear timelines.",
    example: 'In approximately March 2014, while deployed to Bagram with the 173rd ABCT, I first noticed ringing in both ears after a sustained mortar attack on FOB Salerno.',
  },
  "personal:service-event": {
    tip: "Cite specific dates, locations, and units. If you sought sick call, mention it - that creates a record trail.",
    example: 'On 14 June 2015 my vehicle was struck by an IED outside Kandahar. I was treated at the Role 3 hospital and returned to duty 72 hours later. I never had this problem before that day.',
  },
  "personal:symptoms-then": {
    tip: "Describe symptoms in plain language. Avoid medical jargon - reviewers want first-hand description.",
    example: 'Within minutes I had severe ringing in my ears, dizziness, and a headache that lasted three days. I had trouble sleeping for weeks.',
  },
  "personal:continuity": {
    tip: "Continuity of symptoms is critical. If you stopped seeking care, explain why (deployed, no access, stigma, etc.).",
    example: 'The ringing has never gone away. I did not seek care while still on active duty because we were operationally tasked. I started VA care in 2020.',
  },
  "personal:current-impact": {
    tip: "Use concrete examples: missed work days, lost relationships, activities you can no longer do. Quantify when possible.",
    example: 'The ringing wakes me up 3-4 nights per week. I have stopped playing music with my band because I cannot tolerate amplified sound. I missed 6 days of work in the last 90 days.',
  },
  "personal:treatment": {
    tip: 'List EVERYTHING: medications, therapists, doctors, devices, dosages, frequency. VA wants the full picture.',
    example: 'Audiologist Dr. Smith at the Tampa VA every 6 months. Hearing aids issued 02/2022. Trial of Lipoflavonoid 500mg daily.',
  },
  // Lay/witness
  "lay-witness:relationship": {
    tip: "Identify yourself clearly. State how long you've known the veteran and in what capacity.",
    example: 'My name is Sarah Johnson. I am the wife of Sgt John Johnson and have been married to him since 2008, well before his 2010 deployment.',
  },
  "lay-witness:observed-before": {
    tip: 'Establish baseline. What was the veteran like BEFORE the event/service? This is the comparison anchor.',
    example: 'Before John deployed in 2010, he was outgoing, slept soundly, and loved crowded family gatherings. He never had nightmares.',
  },
  "lay-witness:observed-after": {
    tip: 'Describe CHANGES you witnessed. Be specific. Avoid speculation about causes - stick to what you saw.',
    example: 'After he returned in 2011, he could no longer sleep through the night without waking up sweating. He stopped attending family events. He flinched at loud noises in the grocery store.',
  },
  "lay-witness:daily-impact": {
    tip: 'How does this affect daily life from YOUR perspective as someone who lives with the veteran?',
    example: 'He sleeps in the guest room because the nightmares wake me up. He drinks more than he used to. He has missed our daughter\'s last two birthday parties because of crowds.',
  },
  "lay-witness:specific-examples": {
    tip: 'Two or three concrete incidents with dates make this statement powerful. Generalities are weak.',
    example: 'On 4 July 2022 at our backyard cookout, fireworks caused him to drop to the ground and crawl behind a vehicle. On 15 March 2023 he locked himself in the bathroom for three hours after a car backfired.',
  },
  // Nexus
  "nexus:provider-credentials": {
    tip: 'Without credentials, a nexus statement carries little weight. State your full title, license, and years of practice.',
    example: 'I am Dr. Anita Patel, MD, board-certified in Internal Medicine, Florida medical license #ME12345, in clinical practice for 18 years.',
  },
  "nexus:records-reviewed": {
    tip: 'VA wants to know you reviewed the relevant records before forming an opinion.',
    example: 'I reviewed the veteran\'s service treatment records (1998-2008), VA progress notes (2010-present), the 2019 audiogram, and three lay statements from family.',
  },
  "nexus:current-diagnosis": {
    tip: 'State the diagnosis clearly, include ICD-10 code, and explain the clinical basis.',
    example: 'Bilateral sensorineural hearing loss with tinnitus (ICD-10 H90.3, H93.13), confirmed by audiogram dated 10/14/2023.',
  },
  "nexus:service-event": {
    tip: "Tie the diagnosis to a specific event documented in the record.",
    example: 'The veteran reports - and STRs confirm - acoustic trauma exposure during a 2005 IED incident in Iraq (STR p.47).',
  },
  "nexus:nexus-opinion": {
    tip: 'The exact phrase "at least as likely as not" (or "more likely than not") is what VA looks for. Less than that fails the 50% threshold.',
    example: 'It is my medical opinion that the veteran\'s bilateral tinnitus is AT LEAST AS LIKELY AS NOT (50% or greater probability) caused by the documented acoustic trauma in service.',
  },
  "nexus:medical-rationale": {
    tip: 'This is the heart of a nexus letter. Cite mechanism, medical literature, temporal relationship, and rule out other causes.',
    example: 'Acoustic trauma is a well-established cause of cochlear hair cell damage producing sensorineural hearing loss and tinnitus (see Le Prell 2019, Otol Neurotol). The veteran had no pre-service hearing complaints and developed symptoms within hours of the documented blast exposure. There is no occupational or recreational noise exposure post-service that would account for the findings.',
  },
  "nexus:aggravation": {
    tip: 'Only complete if a pre-existing condition was made worse by service. Otherwise leave blank.',
    example: 'The veteran had mild high-frequency hearing loss at entry (audiogram 1998). Post-service audiogram (2010) shows progression of 25 dB across 4-8 kHz, beyond expected age-related decline.',
  },
  "nexus:secondary-connection": {
    tip: 'Only complete if this condition is secondary to another service-connected condition.',
    example: 'The veteran\'s sleep disturbance is at least as likely as not aggravated by his service-connected tinnitus, consistent with the literature on tinnitus-associated insomnia.',
  },
  "nexus:current-severity": {
    tip: 'Help the rater understand how severe this is today - frequency, duration, functional impact.',
    example: 'Tinnitus is constant, bilateral, rated 7/10 by the veteran. It disrupts sleep 4-5 nights per week and prevents him from working in quiet environments.',
  },
  "nexus:signature-block": {
    tip: 'Required. Without signature/date the letter has no legal weight.',
    example: 'Anita Patel, MD\nNPI: 1234567890\nDate: 11/15/2024\n123 Medical Plaza, Tampa FL 33602\n(813) 555-0123 | apatel@clinic.example',
  },
};

export function getPromptCoaching(type, promptId) {
  return statementPromptCoaching[`${type}:${promptId}`] || null;
}

export const statementPrompts = {
  personal: [
    { id: "onset", label: "When did this condition start?", placeholder: "Describe when you first noticed symptoms, what was happening at the time (deployment, duty, training), and the approximate date or timeframe." },
    { id: "service-event", label: "What happened during service?", placeholder: "Describe the specific event, injury, exposure, or duty that caused or worsened this condition. Include dates, locations, unit, and any medical visits." },
    { id: "symptoms-then", label: "What symptoms did you experience at that time?", placeholder: "Describe pain, limitation, mental health changes, or physical symptoms you experienced during or right after the event." },
    { id: "continuity", label: "How have symptoms continued since service?", placeholder: "Explain the continuity: Did symptoms persist? Did they get worse? What treatment have you sought? Any gaps in care and why?" },
    { id: "current-impact", label: "How does this affect your daily life now?", placeholder: "Describe how this impacts work, sleep, relationships, daily tasks, driving, exercise, concentration, or mood. Include frequency of flare-ups." },
    { id: "treatment", label: "What treatment are you receiving?", placeholder: "List current medications, therapies, doctors, VA care, private care, assistive devices (CPAP, brace, etc.)." },
  ],
  "lay-witness": [
    { id: "relationship", label: "What is your relationship to the veteran?", placeholder: "State your name, relationship (spouse, parent, friend, coworker), and how long you have known the veteran." },
    { id: "observed-before", label: "What did you observe before/during service?", placeholder: "Describe the veteran's condition, behavior, or physical ability before or during the relevant time period." },
    { id: "observed-after", label: "What changes did you observe after?", placeholder: "Describe specific changes you personally witnessed: behavior, mood, physical limitations, pain, sleep, social withdrawal." },
    { id: "daily-impact", label: "How does this affect the veteran's daily life?", placeholder: "Describe what you personally observe about how this condition impacts the veteran's work, family, sleep, social life, or daily activities." },
    { id: "specific-examples", label: "Can you give specific examples?", placeholder: "Provide 2-3 specific incidents or observations with approximate dates. Be as factual as possible about what you saw, heard, or experienced." },
  ],
  nexus: [
    { id: "provider-credentials", label: "Provider credentials and qualifications", placeholder: "State your full name, professional title (MD, DO, NP, PA, PhD, LCSW, etc.), state license number(s), specialty, years in practice, and any relevant board certifications." },
    { id: "records-reviewed", label: "Records reviewed and treatment relationship", placeholder: "List records reviewed: service treatment records (STRs), private treatment records, VA medical records, lay statements, imaging, lab work. State how long you have treated or evaluated the veteran." },
    { id: "current-diagnosis", label: "Current diagnosis", placeholder: "State the current diagnosis with ICD-10 code if available, date of diagnosis, and the clinical basis (symptoms, exam findings, imaging, testing) supporting it." },
    { id: "service-event", label: "In-service event, injury, or exposure", placeholder: "Identify the specific in-service event, injury, exposure, or duty condition that is the alleged cause. Cite the documenting STR pages, deployment records, or incident reports." },
    { id: "nexus-opinion", label: 'Nexus opinion (use "at least as likely as not" language)', placeholder: 'State your opinion clearly. Example: "It is my professional opinion that the veteran\'s current [diagnosis] is at least as likely as not (50% or greater probability) caused by, related to, or aggravated by [in-service event/exposure]."' },
    { id: "medical-rationale", label: "Medical rationale supporting the opinion", placeholder: "Explain WHY: cite pathophysiology, mechanism of injury, established medical literature, continuity of symptoms, latency period, exposure dose-response, or differential diagnosis ruling out other causes. This is the most important section." },
    { id: "aggravation", label: "Aggravation (if applicable)", placeholder: "If the condition pre-existed service, state baseline severity and whether service permanently worsened it beyond natural progression. Quantify the increase if possible.", optional: true },
    { id: "secondary-connection", label: "Secondary service connection (if applicable)", placeholder: "If claiming as secondary to a service-connected condition, identify the primary SC condition and explain the causal/aggravation relationship with medical literature support.", optional: true },
    { id: "current-severity", label: "Current severity and functional impact", placeholder: "Describe current symptom severity, frequency of flare-ups, functional limitations, treatment response, and prognosis." },
    { id: "signature-block", label: "Signature, date, and contact information", placeholder: "Provider name, signature, date signed, office address, NPI number, and contact phone/email." },
  ],
};

export function wordCount(text) {
  return String(text || "").trim().split(/\s+/).filter(Boolean).length;
}

// Build the assembled statement text. The OUTPUT is what a doctor / VSO / VA reviewer reads, so
// it intentionally does NOT include the editor's question labels — those are scaffolding for the
// veteran, not headers on the letter. The result reads as a continuous, first-person (or lay /
// medical-provider) narrative composed of the veteran's own paragraphs.
//
// Tone goals:
//   * Human, in the voice of whoever is signing (veteran / witness / clinician).
//   * Objective — the veteran's words are presented verbatim; no boilerplate adjectives,
//     no rating language, no inferences. Only a short factual opener and closing are added.
export function buildStatementText(type, answers) {
  const prompts = statementPrompts[type] || [];
  const filled = prompts
    .map((p) => (answers[p.id] || "").trim())
    .filter(Boolean);
  if (filled.length === 0) return "";

  let opener;
  if (type === "lay-witness") {
    opener = "I am submitting this statement to describe what I have personally observed.";
  } else if (type === "nexus") {
    opener = "This letter sets out my medical opinion regarding the veteran identified below, based on my review of the records listed and my clinical evaluation.";
  } else {
    opener = "I am submitting this statement in my own words, to the best of my recollection.";
  }

  // For the personal statement, lightly cue the final paragraph as a treatment list if the
  // veteran filled that prompt — that one paragraph reads more naturally with a brief lead-in.
  let body = filled;
  if (type === "personal") {
    const treatmentIdx = prompts.findIndex((p) => p.id === "treatment");
    const treatmentText = (answers.treatment || "").trim();
    if (treatmentIdx !== -1 && treatmentText) {
      // Reassemble filled[] but prepend a soft lead-in to the treatment paragraph so it
      // doesn't look like a bare list at the end of a narrative.
      body = prompts
        .map((p) => {
          const v = (answers[p.id] || "").trim();
          if (!v) return null;
          if (p.id === "treatment") return `I am currently receiving the following care: ${v}`;
          return v;
        })
        .filter(Boolean);
    }
  }

  // Signature-block answer (nexus) goes after the closing as its own block.
  let signatureBlock = "";
  if (type === "nexus") {
    const sig = (answers["signature-block"] || "").trim();
    if (sig) {
      // Remove the signature-block paragraph from body so we render it after the closing.
      const sigPromptLabel = prompts.find((p) => p.id === "signature-block")?.label || "";
      body = body.filter((para) => para !== sig);
      signatureBlock = sig;
      // sigPromptLabel intentionally unused — kept for future styling if we want a label.
      void sigPromptLabel;
    }
  }

  const closing = type === "nexus"
    ? "The opinions stated in this letter are based on my professional judgment, review of the records identified above, and accepted medical principles. I am willing to clarify or supplement this opinion on request."
    : "The statements above are true and correct to the best of my knowledge.";

  const parts = [opener, ...body, closing];
  if (signatureBlock) parts.push(signatureBlock);
  return parts.join("\n\n");
}

// ---- Print-mode scrub ----
// Removes any sentence that mentions a specific VA disability rating tier or "rating-fishing" language.
// Used by the Statement Builder's print mode so docs/VSOs don't see a veteran asking for a particular %.
// Returns: { text, removedCount, removedSamples }
export function scrubRatingMentions(text) {
  if (!text) return { text: "", removedCount: 0, removedSamples: [] };
  // Patterns that signal the sentence is talking about a VA rating outcome the veteran is hoping for.
  // - Any percent number ("30%", "30 %")
  // - "X percent" disability/rating
  // - "rated at" / "rate me at" / "give me X" with a number
  // - "I am claiming"/"I deserve"/"deserve a rating" type phrases
  const PATTERNS = [
    /\b\d{1,3}\s*%/i,
    /\b\d{1,3}\s*percent\b/i,
    /\brated\s+at\b/i,
    /\brate(?:\s+\w+)?\s+at\s+\d/i,
    /\bdeserve(?:s)?\s+(?:a\s+)?(?:higher\s+|increased\s+)?rating\b/i,
    /\b(?:fishing|chasing)\s+(?:for\s+)?(?:a\s+)?rating\b/i,
    /\bcombined\s+rating\s+of\s+\d/i,
    /\bservice[- ]connected\s+at\s+\d/i,
    /\bbumped?\s+(?:up|to)\s+\d/i,
  ];
  const removedSamples = [];
  // Split into paragraphs first (preserve blank lines), then sentences within each paragraph.
  const paragraphs = String(text).split(/\n/);
  const cleanedParas = paragraphs.map((para) => {
    if (!para.trim()) return para;
    // Sentence-ish split: keep terminators attached to each chunk.
    const parts = para.match(/[^.!?]+[.!?]+\s*|[^.!?]+$/g) || [para];
    const kept = parts.filter((s) => {
      const hit = PATTERNS.some((re) => re.test(s));
      if (hit) {
        const sample = s.trim();
        if (sample) removedSamples.push(sample.length > 90 ? sample.slice(0, 87) + "..." : sample);
        return false;
      }
      return true;
    });
    return kept.join("").replace(/[ \t]+/g, " ").trim();
  });
  const cleaned = cleanedParas.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  return { text: cleaned, removedCount: removedSamples.length, removedSamples: removedSamples.slice(0, 5) };
}

export function reviewStatementGaps(type, answers) {
  const prompts = statementPrompts[type] || [];
  return prompts
    .filter((p) => !(answers[p.id] || "").trim())
    .map((p) => `Missing: ${p.label}`);
}

export function runSelfTests() {
  const tests = [];
  const add = (name, pass) => tests.push({ name, pass: Boolean(pass) });
  add("50 + 30 VA math rounds to 70", combineRatings([50, 30]).rounded === 70);
  add("No bilateral without paired sides", combineRatingsWithBilateral([{ id: "a", estimatedRating: 10, bilateralSide: "Left", pairedExtremity: "Lower extremities" }]).bilateralApplied === false);
  add("Bilateral applies with L+R lower", combineRatingsWithBilateral([{ id: "l", estimatedRating: 10, bilateralSide: "Left", pairedExtremity: "Lower extremities" }, { id: "r", estimatedRating: 10, bilateralSide: "Right", pairedExtremity: "Lower extremities" }]).bilateralApplied === true);
  add("2026 70% w/ spouse + 3 children", estimateVACompensation(70, "Married", 3).monthly === 2226.45);
  add("High-3 20yrs uses 50% multiplier", estimateHigh3Retirement(6000, 20, 0).monthly === 3000);
  add("Nexus statement type registered", statementTypes.some((t) => t.id === "nexus"));
  add("Lay-witness statement type registered", statementTypes.some((t) => t.id === "lay-witness"));
  return tests;
}

// ---- 526EZ Import Parser / file readers were removed (Import Center is gone). ----
